// Staff moderation and user appeal calls. Every staff call is checked again by the database.

export const OVERDUE_HOURS = 24;

export const reportStatusLabels = { open: 'Open', reviewing: 'Reviewing', actioned: 'Actioned', dismissed: 'Dismissed' };

export const targetTypeLabels = {
  home_job: 'Home job',
  commercial_job: 'Commercial job',
  private_tender: 'Private tender',
  home_job_proposal: 'Bid on a home job',
  job_proposal: 'Bid on a commercial job',
  private_tender_response: 'Tender response',
  user: 'Person',
  organization: 'Company',
  message: 'Message',
  attachment: 'Photo or document',
  review: 'Review',
  work_record: 'Agreed work',
};

export const reasonLabels = { spam: 'Spam', offensive: 'Offensive or abusive', scam: 'Scam or fraud', unsafe: 'Unsafe', other: 'Something else' };

export const actionLabels = {
  report_status: 'Changed report status',
  remove_content: 'Removed content',
  restore_content: 'Restored content',
  suspend_account: 'Suspended account',
  lift_suspension: 'Lifted suspension',
  decide_appeal: 'Decided appeal',
  staff_added: 'Staff access granted',
  staff_removed: 'Staff access removed',
};

export const fieldLabels = {
  title: 'Title', name: 'Name', full_name: 'Name', description: 'Description', message: 'Message', body: 'Message',
  content: 'Content', text: 'Text', comment: 'Comment', response: 'Response', file_name: 'File', filename: 'File',
  original_name: 'File', caption: 'Caption', scope: 'Attached to', mime_type: 'File type', content_type: 'File type',
  size_bytes: 'Size', rating: 'Rating', status: 'Status', category: 'Category', location: 'Area', kind: 'Type',
  proposed_price: 'Price', currency: 'Currency', response_deadline: 'Deadline', created_at: 'Posted',
  updated_at: 'Last changed', hidden_at: 'Hidden', removed_at: 'Removed', verified_at: 'Verified',
};

export const minReasonLength = 3;

export function labelFor(map, key) {
  return map[key] || String(key || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

export function ageHours(createdAt, now = Date.now()) {
  const time = new Date(createdAt).getTime();
  if (Number.isNaN(time)) return 0;
  return Math.max(0, Math.floor((now - time) / 3600000));
}

export function isOverdue(report, now = Date.now()) {
  return ['open', 'reviewing'].includes(report?.status) && ageHours(report.created_at, now) >= OVERDUE_HOURS;
}

export function formatAge(createdAt, now = Date.now()) {
  const hours = ageHours(createdAt, now);
  if (hours < 1) return 'Under an hour';
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} days`;
}

export function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Oldest first, so the reports that have waited longest are handled first. */
export function sortQueue(reports) {
  return [...(reports || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
}

export function validateReason(reason) {
  const value = String(reason || '').trim();
  if (value.length < minReasonLength) return 'Please give a reason of at least 3 characters.';
  if (value.length > 2000) return 'Please keep the reason under 2000 characters.';
  return '';
}

export function validateAppeal(statement) {
  const value = String(statement || '').trim();
  if (value.length < 10) return 'Please explain your appeal in at least 10 characters.';
  if (value.length > 4000) return 'Please keep your appeal under 4000 characters.';
  return '';
}

function requireClient(client) {
  if (!client) throw new Error('Moderation is not connected. Please try again later.');
  return client;
}

async function call(client, name, args) {
  const { data, error } = await requireClient(client).rpc(name, args);
  if (error) throw new Error(error.message);
  return data;
}

export async function checkStaff(client) {
  try {
    const data = await call(client, 'am_i_staff');
    return { staff: Boolean(data?.staff), role: data?.role || null };
  } catch {
    return { staff: false, role: null };
  }
}

export const queueSummary = (client) => call(client, 'moderation_queue_summary');
export const listReports = (client, { status = 'waiting', targetType = null, minAgeHours = null, limit = 100 } = {}) =>
  call(client, 'list_reports', { p_status: status, p_target_type: targetType, p_min_age_hours: minAgeHours, p_limit: limit }).then((rows) => rows || []);
export const setReportStatus = (client, reportId, status, note) =>
  call(client, 'set_report_status', { p_report_id: reportId, p_status: status, p_note: note ?? null });
export const removeContent = (client, { targetType, targetId, reason, reportId = null }) =>
  call(client, 'remove_content', { p_target_type: targetType, p_target_id: targetId, p_reason: reason, p_report_id: reportId });
export const restoreContent = (client, { targetType, targetId, reason }) =>
  call(client, 'restore_content', { p_target_type: targetType, p_target_id: targetId, p_reason: reason });
export const suspendAccount = (client, { userId = null, organizationId = null, reason, endsAt = null, reportId = null }) =>
  call(client, 'suspend_account', { p_reason: reason, p_user_id: userId, p_organization_id: organizationId, p_ends_at: endsAt, p_report_id: reportId });
export const liftSuspension = (client, suspensionId, reason) => call(client, 'lift_suspension', { p_suspension_id: suspensionId, p_reason: reason });
export const listSuspensions = (client, activeOnly = true) => call(client, 'list_suspensions', { p_active_only: activeOnly }).then((rows) => rows || []);
export const listAppeals = (client, status = 'open') => call(client, 'list_appeals', { p_status: status }).then((rows) => rows || []);
export const decideAppeal = (client, appealId, decision, note) => call(client, 'decide_appeal', { p_appeal_id: appealId, p_decision: decision, p_note: note });
export const listActions = (client, { limit = 200, targetType = null, targetId = null } = {}) =>
  call(client, 'list_moderation_actions', { p_limit: limit, p_target_type: targetType, p_target_id: targetId }).then((rows) => rows || []);

export const mySuspensionStatus = (client) => call(client, 'my_suspension_status');
export const submitAppeal = (client, { statement, suspensionId = null, actionId = null }) =>
  call(client, 'submit_appeal', { p_statement: String(statement || '').trim(), p_suspension_id: suspensionId, p_action_id: actionId });
